import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import React, { useMemo, useState } from 'react';

import { Button, Flex, Text } from 'src/client/components';

import { PageRecipient, PageRecipientVisibility } from 'src/commons/types';

import RecipientCard from './components/RecipientCard';
import SelectedRecipients from './components/SelectedRecipients';
import * as S from './styles';

const MAX_RECIPIENTS = 6;

type Props = {
  open: boolean;
  onCancel: () => void;
  pageRecipients: PageRecipient[];
  onSaveSelection: (selectedRecipients: PageRecipient[]) => Promise<void>;
  onToggleTogetherView: (isActive: boolean) => Promise<void>;
};

function TogetherViewModal(props: Props) {
  const {
    open,
    onCancel,
    pageRecipients = [],
    onSaveSelection,
    onToggleTogetherView,
  } = props;

  const screen = useBreakpoint();

  const isMobile = !screen.md;

  const existingTogetherViewFeatured = useMemo(
    () =>
      pageRecipients.filter(
        (recipient) =>
          recipient.visibility ===
          PageRecipientVisibility.TOGETHER_VIEW_FEATURED
      ),
    [pageRecipients]
  );

  const [selectedRecipients, setSelectedRecipients] = useState<PageRecipient[]>(
    existingTogetherViewFeatured
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveRecipient = (recipientId: string) => {
    setSelectedRecipients((prev) =>
      prev.filter((recipient) => recipient.id !== recipientId)
    );
  };

  const handleSelectRecipient = (recipient: PageRecipient) => {
    const isAlreadySelected = selectedRecipients.find(
      (r) => r.id === recipient.id
    );

    if (isAlreadySelected) {
      setSelectedRecipients((prev) =>
        prev.filter((r) => r.id !== recipient.id)
      );
    } else if (selectedRecipients.length < MAX_RECIPIENTS) {
      setSelectedRecipients((prev) => [...prev, recipient]);
    }
  };

  async function handleSaveSelection() {
    if (isLoading || selectedRecipients.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      await onSaveSelection(selectedRecipients);
    } catch (error) {
      console.error('=== TOGETHER VIEW SAVE ERROR ===', error);
    } finally {
      await onToggleTogetherView(true);

      setIsLoading(false);
      onCancel();
    }
  }

  const filteredRecipients = useMemo(() => {
    if (!searchQuery.trim()) {
      return pageRecipients;
    }

    return pageRecipients.filter((recipient) =>
      recipient?.recipientName
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [pageRecipients, searchQuery]);

  const isRecipientSelected = (recipientId: string) =>
    selectedRecipients.some((recipient) => recipient.id === recipientId);

  const maxReached = selectedRecipients.length >= MAX_RECIPIENTS;
  const hasSelections = selectedRecipients.length > 0;

  return (
    <S.StyledModal
      closable={false}
      footer={false}
      open={open}
      zIndex={10000}
      onCancel={onCancel}
    >
      <S.ModalHeaderContainer>
        <S.ModalHeaderContent>
          <S.ModalHeaderTextSection>
            <Flex flexDirection="column">
              <Text type="h2med2">
                Giving Side’s <S.ItalicText>Together View</S.ItalicText>
              </Text>
              <Text type="body2med2">
                Choose up to 6 recipients to feature in your Together View.
              </Text>
              <S.InfoText type="body2med2">
                With this simplified view, we offer you a version of your
                gallery that makes it easy to start conversations about a few of
                the things you care about with others. It’s a starting point
                neither determined by how much or to how many groups you support
                … because we all deserve, and vibrant democracies need us, to be
                celebrated for the choices we make to participate.
              </S.InfoText>
            </Flex>
          </S.ModalHeaderTextSection>

          {!isMobile && (
            <S.ModalHeaderIllustration>
              <S.IllustrationImage
                alt="People illustration"
                src="/together-view-illustration-v2.png"
              />
            </S.ModalHeaderIllustration>
          )}
        </S.ModalHeaderContent>
      </S.ModalHeaderContainer>

      <S.ContentContainer>
        <S.SelectedRecipientsContainer>
          <SelectedRecipients
            selectedRecipients={selectedRecipients}
            onRemoveRecipient={handleRemoveRecipient}
          />
        </S.SelectedRecipientsContainer>

        <S.RecipientSelectionContainer>
          <S.SearchRow>
            <S.RecipientSelectionHeader type="label1med2">
              Select up to {MAX_RECIPIENTS} recipients
            </S.RecipientSelectionHeader>
            <S.SearchContainer>
              <S.SearchInput
                placeholder="Search recipient name"
                suffix={<S.SearchIcon />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </S.SearchContainer>
          </S.SearchRow>

          <S.ScrollableGridContainer>
            <S.RecipientsGrid>
              {filteredRecipients.map((recipient) => {
                const isSelected = isRecipientSelected(recipient.id);
                const isDisabled = !isSelected && maxReached;

                return (
                  <RecipientCard
                    isDisabled={isDisabled}
                    isSelected={isSelected}
                    key={recipient.id}
                    recipient={recipient}
                    onClick={() => handleSelectRecipient(recipient)}
                  />
                );
              })}
            </S.RecipientsGrid>
          </S.ScrollableGridContainer>
        </S.RecipientSelectionContainer>

        <S.InfoText type="body2med2">
          You can return to this view anytime to update your selection.
        </S.InfoText>
      </S.ContentContainer>

      <S.FooterButtonGroup>
        <Button type="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!hasSelections}
          loading={isLoading}
          type="primary"
          onClick={handleSaveSelection}
        >
          Save selection
        </Button>
      </S.FooterButtonGroup>
    </S.StyledModal>
  );
}

export default TogetherViewModal;
